/**
 * The NHRA safety-equipment starter — "add usual safety equipment".
 *
 * Given a car's or driver's elapsed time and trap speed, this pre-selects the
 * gear the NHRA E.T. brackets call for at that pace, so a racer sets up a
 * plausible safety list in one gesture instead of adding a dozen items by hand.
 *
 * It is a STARTING POINT, not the rulebook. The exact requirements turn on class,
 * body style and sanctioning body, and the rulebook is revised every year — so
 * the sheet says as much and points at a certified NHRA chassis inspector. The
 * thresholds below follow the published NHRA quarter-mile E.T. brackets (an
 * eighth-mile racer enters their *projected* quarter-mile ET); a rule fires when
 * the ET is at or under its cutoff **or** the trap speed is at or over it.
 *
 * Each rule renders as a toggle, pre-checked when it `applies` — so a racer can
 * still add a piece their ET does not strictly require, or drop one they handle
 * differently. Nothing here is created until they confirm.
 */

import type { CreateItemRequest, OwnerType, System } from '@svcdue/contracts';

export interface SafetyOwner {
  ownerType: OwnerType;
  ownerId: string;
  /** The car's nickname or the driver's name — what the sheet is titled with. */
  label: string;
}

export interface SafetyRule {
  key: string;
  owner: OwnerType;
  name: string;
  system: System;
  /** The SFI / Snell spec this piece carries at this tier. */
  spec: string;
  /** One line on why the brackets call for it, e.g. "9.99 or quicker, or 135+ mph". */
  why: string;
  /** True when the NHRA brackets require it at this quarter-mile ET / trap speed. */
  applies: (et: number, mph: number) => boolean;
}

/**
 * The ladder, quickest-requirement first within each owner. The bands are made
 * non-overlapping on purpose: a roll cage supersedes a roll bar, and a full suit
 * supersedes a single-layer jacket, so only one of each pair is ever pre-checked.
 */
export const SAFETY_RULES: SafetyRule[] = [
  // --- on the car: chassis & restraint ------------------------------------
  {
    key: 'rollbar',
    owner: 'car',
    name: 'Roll bar',
    system: 'Chassis',
    spec: 'NHRA / SFI · min. 5-point',
    why: '11.49–10.00 ET (13.49 in a convertible)',
    applies: (et, mph) => et <= 11.49 && et > 9.99 && mph < 135,
  },
  {
    key: 'rollcage',
    owner: 'car',
    name: 'Roll cage',
    system: 'Chassis',
    spec: 'NHRA cert · 3-yr recert',
    why: '9.99 or quicker, or 135+ mph',
    applies: (et, mph) => et <= 9.99 || mph >= 135,
  },
  {
    key: 'harness',
    owner: 'car',
    name: 'Driver harness',
    system: 'Safety',
    spec: 'SFI 16.1 · 2-yr recert',
    why: 'any car with a roll bar or cage',
    applies: (et, mph) => et <= 11.49 || mph >= 135,
  },
  {
    key: 'windownet',
    owner: 'car',
    name: 'Window net',
    system: 'Chassis',
    spec: 'SFI 27.1',
    why: '9.99 or quicker, or 135+ mph (full-bodied)',
    applies: (et, mph) => et <= 9.99 || mph >= 135,
  },

  // --- on the driver: personal gear ---------------------------------------
  {
    key: 'helmet',
    owner: 'driver',
    name: 'Helmet',
    system: 'Safety',
    spec: 'Snell SA2020',
    why: '13.99 or quicker',
    applies: (et, mph) => et <= 13.99 || mph >= 135,
  },
  {
    key: 'jacket',
    owner: 'driver',
    name: 'Fire jacket',
    system: 'Safety',
    spec: 'SFI 3.2A/1',
    why: '11.99–10.00 ET',
    applies: (et, mph) => et <= 11.99 && et > 9.99 && mph < 135,
  },
  {
    // Named to match the Add Gear suit vocabulary ("Racing suit — <variant>") so
    // an item this creates round-trips cleanly when the racer opens it to set its
    // recert date, rather than being unrecognised and silently renamed.
    key: 'suit',
    owner: 'driver',
    name: 'Racing suit — Jacket & pants',
    system: 'Safety',
    spec: 'SFI 3.2A/5',
    why: '9.99 or quicker, or 135+ mph',
    applies: (et, mph) => et <= 9.99 || mph >= 135,
  },
  {
    key: 'gloves',
    owner: 'driver',
    name: 'Gloves',
    system: 'Safety',
    spec: 'SFI 3.3/5',
    why: '9.99 or quicker, or 135+ mph',
    applies: (et, mph) => et <= 9.99 || mph >= 135,
  },
  {
    key: 'shoes',
    owner: 'driver',
    name: 'Shoes',
    system: 'Safety',
    spec: 'SFI 3.3/5',
    why: '9.99 or quicker, or 135+ mph',
    applies: (et, mph) => et <= 9.99 || mph >= 135,
  },
  {
    key: 'neckcollar',
    owner: 'driver',
    name: 'Neck collar',
    system: 'Safety',
    spec: 'SFI 3.3',
    why: '9.99–7.50 ET (unless using a 38.1 device)',
    applies: (et, mph) => et <= 9.99 && et > 7.49 && mph < 150,
  },
  {
    key: 'hans',
    owner: 'driver',
    name: 'Head & neck restraint',
    system: 'Safety',
    spec: 'SFI 38.1',
    why: '7.49 or quicker, or 150+ mph',
    applies: (et, mph) => et <= 7.49 || mph >= 150,
  },
];

/**
 * The working inspector locator.
 *
 * Was `cms.nhra.com/chassisinspectors/`, which now serves an expired certificate
 * — so the one link on the sheet that sends a racer to a real person was landing
 * them on a browser security warning. `nhra.com/chassis-inspector-locator` is
 * the linked-to page but renders empty; this host is the tool that actually
 * works. Shared with ClassCalc via `classcalc/sources.ts`.
 */
export const NHRA_INSPECTOR_URL = 'https://chassisinspector.nhradata.com/';

export function rulesForOwner(ownerType: OwnerType): SafetyRule[] {
  return SAFETY_RULES.filter((r) => r.owner === ownerType);
}

/**
 * Which rules the brackets recommend at this ET / trap speed — the set the sheet
 * pre-checks. Empty until at least one number is entered: a missing ET counts as
 * "infinitely slow" so only the speed can trigger, and a missing speed as zero.
 */
export function recommended(ownerType: OwnerType, et: number | null, mph: number | null): Set<string> {
  const e = et ?? Infinity;
  const m = mph ?? 0;
  return new Set(rulesForOwner(ownerType).filter((r) => r.applies(e, m)).map((r) => r.key));
}

/**
 * A rule → the item to create. Safety gear carries **no check condition** — its
 * recert / replace date is what matters, and that is what drives the reminders
 * (§6). The date is deliberately left blank for the racer to set (a helmet's
 * Snell year, a suit's 5-year recert, the restraint's replace-by): inventing it
 * would fire reminders off a number nobody entered. So every item comes in as a
 * plain safety item with its spec, and no triggers.
 */
export function toItemRequest(rule: SafetyRule, owner: SafetyOwner): CreateItemRequest {
  return {
    ownerType: owner.ownerType,
    ownerId: owner.ownerId,
    name: rule.name,
    partNumber: null,
    system: rule.system,
    buyUrl: null,
    isSafety: true,
    expirationDate: null,
    spec: rule.spec,
    lastServicedAt: null,
    triggers: [],
  };
}
